import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Tag,
  Bot,
  ShieldAlert,
  MapPin,
  Download,
  FolderOpen,
  ArrowRight,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import SiteSwitcher from "@/components/ruby-ai-v2/dashboard/SiteSwitcher";
import ReportSwitcher from "@/components/ruby-ai-v2/dashboard/ReportSwitcher";
import { useSiteContext } from "@/components/ruby-ai-v2/contexts/SiteContext";
import { useMCPContext } from "@/components/ruby-ai-v2/hooks/useMCPContext";
import type { CardContext } from "@/components/ruby-ai-v2/services/mcpService";

// ── Brand gradient (violet → orange), matches the OG01 website heading move ──
const BRAND_GRADIENT = "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)";
const gradientText: React.CSSProperties = {
  background: BRAND_GRADIENT,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const PRIORITY_COLOR: Record<"HIGH" | "MEDIUM" | "LOW", string> = {
  HIGH: "#EF4444",
  MEDIUM: "#F59E0B",
  LOW: "#22C55E",
};

const CARD_CONFIG: {
  id: string;
  route: string;
  title: string;
  description: string;
  color: string;
  icon: LucideIcon;
}[] = [
  {
    id: "search-intelligence",
    route: "/ruby-aissist/search-intelligence",
    title: "Search Intelligence",
    description:
      "Structural authority, keyword positioning, competitive search landscape, and technical health signals.",
    color: "#8B5CF6",
    icon: Search,
  },
  {
    id: "brand-insight",
    route: "/ruby-aissist/brand-insight",
    title: "Brand Intelligence",
    description:
      "Entity consistency, brand citation frequency, cross-platform signal alignment, and AI mention tracking.",
    color: "#FF7A18",
    icon: Tag,
  },
  {
    id: "ai-readiness",
    route: "/ruby-aissist/ai-readiness",
    title: "AI Readiness",
    description:
      "Structured data coverage, content interpretability, topical authority depth, and entity relationship clarity.",
    color: "#3B82F6",
    icon: Bot,
  },
  {
    id: "ai-vulnerability",
    route: "/ruby-aissist/ai-vulnerability",
    title: "AI Risk & Exposure",
    description:
      "Security exposure, entity conflicts, toxic link profiles, duplicate content, and suppression signals. Higher is healthier.",
    color: "#EF4444",
    icon: ShieldAlert,
  },
  {
    id: "local-intelligence",
    route: "/ruby-aissist/local-intelligence",
    title: "Local Intelligence",
    description:
      "Google Business Profile completeness, citation count, NAP consistency, review depth, and local pack visibility.",
    color: "#22C55E",
    icon: MapPin,
  },
];

const GAUGE_RADIUS = 72;
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS;

function formatTimeAgo(isoDate?: string): string {
  if (!isoDate) return "—";
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

function buildCardMetrics(card: CardContext | undefined) {
  const issues = card?.issues ?? { high: 0, medium: 0, low: 0, total: 0, fixed: 0 };
  const pending = Math.max(0, issues.total - issues.fixed);
  const priority: "HIGH" | "MEDIUM" | "LOW" =
    pending > 0
      ? issues.high > 0
        ? "HIGH"
        : issues.medium > 0
          ? "MEDIUM"
          : "LOW"
      : "LOW";

  return {
    pending,
    priority,
    metrics: [
      { name: "Authority band", value: card?.band || "—" },
      { name: "Open actions", value: pending > 0 ? `${pending} pending` : "None" },
      { name: "Top priority", value: pending > 0 ? priority : "—" },
    ],
  };
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { activeSite, selectedReportId } = useSiteContext();
  const domain = activeSite?.domain || "";

  const { overview, isLoading, error, refresh } = useMCPContext({ domain, reportId: selectedReportId });

  const composite = useMemo(() => {
    if (!overview) {
      return {
        score: 0,
        delta: 0,
        tier: "—",
        domain: domain || "No site selected",
        description: domain
          ? "Run an authority audit to see your authority rating."
          : "Add a site using the domain switcher to get started.",
      };
    }

    const { compositeScore, recommendedReason } = overview;
    const hasScore = compositeScore.score > 0;
    const description =
      recommendedReason && recommendedReason !== "No pending actions from your audit"
        ? recommendedReason
        : hasScore
          ? `Your authority band is ${compositeScore.band}. Open a Smart Card below for detailed analysis and Ruby fixes.`
          : "Run an authority audit to populate your authority profile.";

    return {
      score: compositeScore.score,
      delta: compositeScore.change ?? 0,
      tier: compositeScore.band || "—",
      domain: overview.domain,
      description,
    };
  }, [overview, domain]);

  const dimensions = useMemo(
    () =>
      CARD_CONFIG.map((cfg) => ({
        name: cfg.title,
        score: overview?.cards[cfg.id]?.score ?? 0,
        color: cfg.color,
      })),
    [overview]
  );

  const smartCards = useMemo(
    () =>
      CARD_CONFIG.map((cfg) => {
        const card = overview?.cards[cfg.id];
        const { pending, priority, metrics } = buildCardMetrics(card);

        return {
          ...cfg,
          score: card?.score ?? 0,
          metrics,
          issues: { count: pending, priority },
        };
      }),
    [overview]
  );

  const lastRun = formatTimeAgo(overview?.lastAuditDate || activeSite?.lastAuditAt);

  return (
    <div className="animate-fade-in px-6 pb-14 pt-6 md:px-8">
      <div className="mx-auto w-full max-w-[1320px]">
        {/* ── Page toolbar: domain switcher · meta · actions ── */}
        <div className="mb-8 flex flex-wrap items-center gap-3.5">
          <SiteSwitcher />
          <ReportSwitcher />

          <div className="hidden items-center gap-[18px] text-[12.5px] text-og01-text-muted sm:flex">
            <span>
              Market: <b className="font-medium text-og01-text-secondary">AU</b>
            </span>
            <span>
              Tier: <b className="font-medium text-og01-text-secondary">{composite.tier}</b>
            </span>
            <span>
              Last run: <b className="font-medium text-og01-text-secondary">{lastRun}</b>
            </span>
          </div>

          <div className="flex-1" />

          <button
            disabled={!overview}
            className="inline-flex items-center gap-2 rounded-[10px] border border-white/[0.07] bg-og01-card px-4 py-2 text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Download className="h-[15px] w-[15px]" />
            Export Full Report
          </button>
          <button
            onClick={() => navigate("/resource-vault")}
            className="inline-flex items-center gap-2 rounded-[10px] border-none px-4 py-2 text-[13.5px] font-semibold text-white"
            style={{ background: BRAND_GRADIENT, boxShadow: "0 8px 26px -12px rgba(255,122,24,.65)" }}
          >
            <FolderOpen className="h-[15px] w-[15px]" />
            Saved Reports
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* ── Authority hero (composite score) ── */}
        <div className="mb-2 inline-flex items-center gap-2.5 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-og01-violet">
          <span className="h-[1.5px] w-6" style={{ background: BRAND_GRADIENT }} />
          Your Authority Rating
        </div>

        <section className="relative mb-10 grid items-center gap-8 overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card p-7 lg:grid-cols-[auto_1px_1fr]">
          <div
            className="pointer-events-none absolute -right-[6%] -top-[40%] h-[340px] w-[340px]"
            style={{ background: "radial-gradient(circle, rgba(139,92,246,.14), transparent 65%)" }}
          />

          {isLoading ? (
            <div className="col-span-full flex items-center justify-center gap-3 py-16 text-og01-text-secondary">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading authority profile…
            </div>
          ) : (
            <>
              {/* Gauge */}
              <div className="relative mx-auto h-[168px] w-[168px]">
                <svg width="168" height="168" viewBox="0 0 168 168" className="-rotate-90">
                  <defs>
                    <linearGradient id="gauge-grad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#8B5CF6" />
                      <stop offset=".5" stopColor="#B16CEA" />
                      <stop offset="1" stopColor="#FF7A18" />
                    </linearGradient>
                  </defs>
                  <circle cx="84" cy="84" r={GAUGE_RADIUS} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="12" />
                  <circle
                    cx="84"
                    cy="84"
                    r={GAUGE_RADIUS}
                    fill="none"
                    stroke="url(#gauge-grad)"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={GAUGE_CIRCUMFERENCE}
                    strokeDashoffset={GAUGE_CIRCUMFERENCE * (1 - composite.score / 100)}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-heading text-[56px] leading-none text-og01-text">{composite.score || "—"}</div>
                  <div className="mt-1 text-[10.5px] uppercase tracking-[0.16em] text-og01-text-muted">Overall</div>
                </div>
                {composite.delta !== 0 && (
                  <div
                    className="absolute top-1.5 left-1/2 translate-x-[34px] text-[13px] font-semibold"
                    style={{ color: composite.delta > 0 ? PRIORITY_COLOR.LOW : PRIORITY_COLOR.HIGH }}
                  >
                    {composite.delta > 0 ? "+" : ""}
                    {composite.delta}
                  </div>
                )}
              </div>

              <div className="hidden self-stretch bg-white/[0.07] lg:block" />

              <div className="relative">
                <div className="mb-1 flex flex-wrap items-baseline gap-3">
                  <span
                    className="rounded-full border px-2.5 py-[5px] text-xs font-semibold"
                    style={{ color: "#3B82F6", background: "rgba(59,130,246,.12)", borderColor: "rgba(59,130,246,.28)" }}
                  >
                    {composite.tier}
                  </span>
                  <span className="text-[15px] font-semibold text-og01-text">{composite.domain}</span>
                </div>
                {overview?.reportNotice && (
                  <p className="mb-3 max-w-[62ch] text-[13px] text-amber-300">{overview.reportNotice}</p>
                )}
                <p className="mb-5 max-w-[62ch] text-[13.5px] text-og01-text-secondary">{composite.description}</p>

                <div className="grid gap-x-7 gap-y-2 sm:grid-cols-2">
                  {dimensions.map((dim) => (
                    <div
                      key={dim.name}
                      className="flex items-center gap-3 rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-3.5 py-[11px]"
                    >
                      <span className="h-2 w-2 flex-none rounded-full" style={{ background: dim.color }} />
                      <span className="flex-1 text-[13px] text-og01-text-secondary">{dim.name}</span>
                      <span className="h-1 w-[54px] flex-none overflow-hidden rounded-[3px] bg-white/[0.07]">
                        <i className="block h-full rounded-[3px]" style={{ width: `${dim.score}%`, background: dim.color }} />
                      </span>
                      <span className="font-heading text-[22px] leading-none" style={{ color: dim.color }}>
                        {dim.score || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>

        {/* ── Smart cards section header ── */}
        <div className="mb-[22px] flex items-end justify-between gap-6">
          <div>
            <div className="mb-2 inline-flex items-center gap-2.5 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-og01-violet">
              <span className="h-[1.5px] w-6" style={{ background: BRAND_GRADIENT }} />
              Smart Cards
            </div>
            <h2 className="font-heading text-[clamp(28px,3.4vw,42px)] font-normal leading-[1.05] text-og01-text">
              Five dimensions.
              <br />
              One <em style={gradientText}>Authority Rating.</em>
            </h2>
          </div>
          <p className="hidden max-w-[46ch] text-right text-[13.5px] text-og01-text-secondary md:block">
            Each Smart Card scores one dimension of your digital authority, with the open issues Ruby will fix first.
          </p>
        </div>

        {/* ── Smart card grid ── */}
        <div className="grid grid-cols-1 gap-[18px] md:grid-cols-2 lg:grid-cols-3">
          {smartCards.map((card) => {
            const Icon = card.icon;
            const pri = PRIORITY_COLOR[card.issues.priority];
            return (
              <article
                key={card.id}
                tabIndex={0}
                onClick={() => navigate(card.route)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(card.route);
                  }
                }}
                className="group relative flex cursor-pointer flex-col rounded-2xl border border-white/[0.07] bg-og01-card p-[22px] pb-[18px] transition-all duration-200 hover:-translate-y-[3px] hover:border-white/[0.12] hover:bg-og01-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-og01-purple"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div className="grid h-[42px] w-[42px] flex-none place-items-center rounded-[11px] border border-white/[0.07] bg-[#0F0F18]">
                    <Icon className="h-5 w-5" style={{ color: card.color }} />
                  </div>
                  <div className="font-heading text-[38px] leading-[0.9]" style={{ color: card.color }}>
                    {isLoading ? "…" : card.score || "—"}
                  </div>
                </div>

                <h3 className="mb-1.5 text-base font-semibold text-og01-text">{card.title}</h3>
                <p className="mb-4 min-h-[54px] text-[12.8px] leading-[1.45] text-og01-text-secondary">
                  {card.description}
                </p>

                <div className="mb-4 flex flex-col gap-[9px]">
                  {card.metrics.map((m) => (
                    <div key={m.name} className="flex items-center gap-[9px] text-[13px]">
                      <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ background: card.color }} />
                      <span className="flex-1 text-og01-text-secondary">{m.name}</span>
                      <span className="font-semibold text-og01-text">{m.value}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto flex items-center justify-between border-t border-white/[0.07] pt-3.5">
                  <span className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold" style={{ color: card.color }}>
                    Open Card
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-[3px]" />
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold"
                    style={{ color: pri, background: `${pri}1A` }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: pri }} />
                    {card.issues.count} to fix
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

